import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DURATIONS } from "./ccApi";

/* Part 7 — every moderation action funnels through this dialog: written
 * reason (required), optional duration presets, explicit confirm for
 * high-impact actions. The server re-validates all three. */
export default function ReasonDialog({ spec, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setConfirm(false); setHours(24); setBusy(false); }, [spec]);
  if (!spec) return null;
  const needsConfirm = spec.highImpact || (spec.duration && hours === null);
  const ok = reason.trim().length >= 5 && (!needsConfirm || confirm);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit({ reason: reason.trim(), confirm, hours: spec.duration ? hours : undefined }); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cc-reason-dialog" className="bg-zinc-950 border-white/10 text-white sm:max-w-md z-[200]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{spec.title}</DialogTitle>
          {spec.description && <DialogDescription className="text-zinc-400">{spec.description}</DialogDescription>}
        </DialogHeader>
        {spec.duration && (
          <div className="flex flex-wrap gap-2" data-testid="cc-duration-presets">
            {DURATIONS.map((d) => (
              <button key={d.label} type="button" data-testid={`cc-duration-${d.label}`} onClick={() => setHours(d.hours)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${hours === d.hours ? "bg-rose-600 border-rose-500 text-white" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>
                {d.label}
              </button>
            ))}
          </div>
        )}
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-widest text-zinc-500">Reason (required, logged)</label>
          <Textarea data-testid="cc-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Why are you taking this action? Minimum 5 characters." className="bg-black/40 border-white/10 text-white" />
        </div>
        {needsConfirm && (
          <label className="flex items-start gap-3 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 p-3 rounded-md cursor-pointer">
            <Checkbox data-testid="cc-confirm-checkbox" checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} className="mt-0.5 border-amber-400" />
            <span>High-impact action. I confirm this is intended and will be permanently recorded in the audit timeline.</span>
          </label>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="cc-reason-cancel" className="text-zinc-300">Cancel</Button>
          <Button onClick={submit} disabled={!ok || busy} data-testid="cc-reason-submit"
            className={spec.highImpact ? "bg-rose-600 hover:bg-rose-500" : "bg-white text-black hover:bg-zinc-200"}>
            {busy ? "Working…" : spec.cta || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
